import { defineComponent, reactive, ref, computed, toRef, resolveComponent, openBlock, createElementBlock, normalizeClass, createBlock, withCtx, renderSlot, createTextVNode, toDisplayString, createCommentVNode, createVNode, withDirectives, createElementVNode, mergeProps, vModelText, createSlots, vShow } from 'vue';
import { s as styleInject } from '../OrInlineInputV3-061441a3.js';
import { useTextareaAutosize } from '@vueuse/core';
import { K, _, c as ie, w as _$1, r as re } from '../OrAutocompleteV3-8c6de221.js';
import { InputBoxSize, OrInputBoxV3 as script$3, InputBoxVariant } from '../or-input-box-v3/index.js';
import { OrErrorV3 as script$1 } from '../or-error-v3/index.js';
import { OrHintV3 as script$2 } from '../or-hint-v3/index.js';
import { OrLabelV3 as script$4 } from '../or-label-v3/index.js';
import '../or-inline-input-box-v3/index.js';
import '../or-text-v3/index.js';
import '../or-icon-button-v3/index.js';
import '../or-icon-v3/index.js';
import '../or-tooltip-v3/index.js';
import '../or-popover-v3/index.js';
import '@floating-ui/dom';
import '../or-bottom-sheet-v3/index.js';
import '../or-overlay-v3/index.js';
import '../or-teleport-v3/index.js';
import '@onereach/styles/screens.json';
import '@onereach/styles/tailwind.config.json';
import 'lodash/get';
import 'lodash/isElement';
import '../or-input-v3/index.js';
import '../or-loader-v3/index.js';
import '../or-menu-item-v3/index.js';
import '../or-tag-input-v3/index.js';
import '../or-tags-v3/index.js';
import '../or-button-v3/index.js';
import '../or-tag-v3/index.js';

const Textarea = [
// Layout
'layout-column',
// Spacing
'gap-xs'];
const TextareaControl = [
// Box
'w-full',
// Typography
'typography-inherit',
// Theme (placeholder)
'placeholder:theme-foreground-outline', 'dark:placeholder:theme-foreground-outline-dark'];
const TextareaControlSizes = {
  [InputBoxSize.M]: [
  // Spacing
  '!mx-0', 'px-md md:px-sm+', 'py-sm+* md:py-sm*'],
  [InputBoxSize.S]: [
  // Spacing
  '!mx-0', 'px-md md:px-sm+', 'py-sm*']
};

var script = defineComponent({
  name: 'OrTextarea',
  components: {
    OrError: script$1,
    OrHint: script$2,
    OrInputBox: script$3,
    OrLabel: script$4
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: undefined,
      default: undefined
    },
    name: {
      type: String,
      default: undefined
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    variant: {
      type: String,
      default: undefined
    },
    size: {
      type: String,
      default: () => InputBoxSize.M
    },
    rows: {
      type: [Number, Object],
      default: 2
    },
    label: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    },
    hint: {
      type: String,
      default: undefined
    },
    error: {
      type: [String, Boolean],
      default: undefined
    },
    required: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'input', 'keydown', 'keyup', 'focus', 'blur'],
  expose: ['root', 'focus', 'blur'],
  setup(props, context) {
    const attributes = reactive({
      id: K(),
      ..._(),
      ...ie()
    });
    // Refs & Styles
    const root = ref();
    const rootStyles = computed(() => ['or-textarea-v3', ...Textarea, ...(props.variant === InputBoxVariant.Embedded ? ['grow'] : [])]);
    const control = ref();
    const controlStyles = computed(() => [...TextareaControl, ...TextareaControlSizes[props.size], ...(_$1(props.rows) ? ['resize-none'] : [])]);
    const controlInlineStyles = computed(() => {
      if (control.value) {
        const paddingTop = Number(getComputedStyle(control.value).getPropertyValue('padding-top').match(/^(?<value>\d+)px$/).groups.value);
        const paddingBottom = Number(getComputedStyle(control.value).getPropertyValue('padding-bottom').match(/^(?<value>\d+)px$/).groups.value);
        const lineHeight = Number(getComputedStyle(control.value).getPropertyValue('line-height').match(/^(?<value>\d+)px$/).groups.value);
        return {
          minHeight: `${paddingTop + paddingBottom + (_$1(props.rows) ? props.rows.min : props.rows) * lineHeight}px`,
          maxHeight: _$1(props.rows) && props.rows.max ? `${paddingTop + paddingBottom + props.rows.max * lineHeight}px` : undefined
        };
      }
      return {};
    });
    // State
    const model = re(toRef(props, 'modelValue'), context.emit, {
      defaultValue: ref('')
    });
    const length = computed(() => {
      var _a, _b;
      return (_b = (_a = model.value) === null || _a === void 0 ? void 0 : _a.toString().length) !== null && _b !== void 0 ? _b : 0;
    });
    const maxLength = computed(() => {
      return attributes.maxlength;
    });
    // Methods
    function focus() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.focus();
    }
    function blur() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.blur();
    }
    // Effects
    if (_$1(props.rows)) {
      // Unluckily, `useTextareaAutosize` doesn't have a teardown function
      // So, dynamically changed `rows` won't have any effect
      useTextareaAutosize({
        element: control,
        input: model
      });
    }
    return {
      attributes,
      root,
      rootStyles,
      control,
      controlStyles,
      controlInlineStyles,
      model,
      length,
      maxLength,
      focus,
      blur
    };
  }
});

const _hoisted_1 = ["name", "rows", "autocomplete", "placeholder", "required", "readonly", "disabled"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrLabel = resolveComponent("OrLabel");
  const _component_OrInputBox = resolveComponent("OrInputBox");
  const _component_OrHint = resolveComponent("OrHint");
  const _component_OrError = resolveComponent("OrError");
  return openBlock(), createElementBlock("div", {
    ref: 'root',
    class: normalizeClass(_ctx.rootStyles)
  }, [_ctx.$slots.label || _ctx.$slots.addon || _ctx.label ? (openBlock(), createBlock(_component_OrLabel, {
    key: 0,
    "control-id": _ctx.attributes.id,
    variant: 'input',
    required: _ctx.required,
    disabled: _ctx.disabled
  }, {
    addon: withCtx(() => [renderSlot(_ctx.$slots, "addon")]),
    default: withCtx(() => [renderSlot(_ctx.$slots, "label", {}, () => [createTextVNode(toDisplayString(_ctx.label), 1 /* TEXT */)])]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["control-id", "required", "disabled"])) : createCommentVNode("v-if", true), createVNode(_component_OrInputBox, {
    variant: _ctx.variant,
    size: _ctx.size,
    invalid: !!_ctx.error,
    readonly: _ctx.readonly,
    disabled: _ctx.disabled
  }, {
    default: withCtx(() => [withDirectives(createElementVNode("textarea", mergeProps({
      ref: 'control'
    }, _ctx.attributes, {
      "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => _ctx.model = $event),
      class: _ctx.controlStyles,
      style: _ctx.controlInlineStyles,
      name: _ctx.name,
      rows: _ctx.rows,
      autocomplete: _ctx.autocomplete,
      placeholder: _ctx.placeholder,
      required: _ctx.required,
      readonly: _ctx.readonly,
      disabled: _ctx.disabled,
      onInput: _cache[1] || (_cache[1] = $event => _ctx.$emit('input', $event)),
      onKeydown: _cache[2] || (_cache[2] = $event => _ctx.$emit('keydown', $event)),
      onKeyup: _cache[3] || (_cache[3] = $event => _ctx.$emit('keyup', $event)),
      onFocus: _cache[4] || (_cache[4] = $event => _ctx.$emit('focus', $event)),
      onBlur: _cache[5] || (_cache[5] = $event => _ctx.$emit('blur', $event))
    }), null, 16 /* FULL_PROPS */, _hoisted_1), [[vModelText, _ctx.model, void 0, {
      trim: true
    }]])]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["variant", "size", "invalid", "readonly", "disabled"]), _ctx.$slots.hint || _ctx.hint || _ctx.maxLength ? withDirectives((openBlock(), createBlock(_component_OrHint, {
    key: 1,
    disabled: _ctx.disabled
  }, createSlots({
    default: withCtx(() => [renderSlot(_ctx.$slots, "hint", {}, () => [createTextVNode(toDisplayString(_ctx.hint), 1 /* TEXT */)])]),
    _: 2 /* DYNAMIC */
  }, [_ctx.maxLength ? {
    name: "addon",
    fn: withCtx(() => [createTextVNode(toDisplayString(_ctx.length) + " / " + toDisplayString(_ctx.maxLength), 1 /* TEXT */)]),
    key: "0"
  } : undefined]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["disabled"])), [[vShow, !_ctx.error]]) : createCommentVNode("v-if", true), withDirectives(createVNode(_component_OrError, {
    disabled: _ctx.disabled
  }, createSlots({
    default: withCtx(() => [createTextVNode(toDisplayString(_ctx.error) + " ", 1 /* TEXT */)]),
    _: 2 /* DYNAMIC */
  }, [_ctx.maxLength ? {
    name: "addon",
    fn: withCtx(() => [createTextVNode(toDisplayString(_ctx.length) + " / " + toDisplayString(_ctx.maxLength), 1 /* TEXT */)]),
    key: "0"
  } : undefined]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["disabled"]), [[vShow, typeof _ctx.error === 'string' && _ctx.error.length > 0]])], 2 /* CLASS */);
}

var css_248z = "\ntextarea[data-v-10a22fb4] {\n  min-width: 0;\n  background: none;\n  outline: none;\n}\n";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-10a22fb4";
script.__file = "src/components/or-textarea-v3/OrTextarea.vue";

export { script as OrTextareaV3 };
